import React, { useState, useEffect } from 'react';
import { BookOpen, MapPin, Save, ArrowLeft, UserPlus, Maximize2, Minimize2, X, Pencil } from 'lucide-react';
import api from '../api';

const CreateMateriaMenu = ({ onClose, editData }) => {
  const [loading, setLoading] = useState(false);
  const [diplomaturas, setDiplomaturas] = useState([]);
  const [profesores, setProfesores] = useState([]);
  const [formData, setFormData] = useState({
    nombreMateria: editData ? editData.name : '',
    diplomaturaId: editData ? editData.diploma : '',
    teachers: editData ? (editData.teachers || []) : [], // Array of IDs
    manualEmails: [], // Array of strings
    searchEmail: '' // For manual entry input
  });

  const [isEditing, setIsEditing] = useState(!!editData);
  const [selectedSubjectId, setSelectedSubjectId] = useState(editData ? editData.id : null);
  const [activeTab, setActiveTab] = useState(editData ? 'form' : 'tabs'); // 'tabs', 'form', 'list'
  const [searchTerm, setSearchTerm] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [showManualInput, setShowManualInput] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [diplomasRes, usersRes, subjectsRes] = await Promise.all([
        api.get('diplomas/'),
        api.get('users/'),
        api.get('subjects/')
      ]);
      setDiplomaturas(diplomasRes.data);
      setProfesores(usersRes.data.filter(u => u.role === 'DO' || u.role === 'CD'));
      setSubjects(subjectsRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  };

  const toggleTeacher = (teacherId) => {
    const current = [...formData.teachers];
    if (current.includes(teacherId)) {
      setFormData({ ...formData, teachers: current.filter(id => id !== teacherId) });
    } else {
      setFormData({ ...formData, teachers: [...current, teacherId] });
    }
  };

  const addManualEmail = () => {
    if (!formData.searchEmail) return;
    if (formData.manualEmails.includes(formData.searchEmail)) {
        setFormData({...formData, searchEmail: ''});
        return;
    }
    setFormData({
      ...formData,
      manualEmails: [...formData.manualEmails, formData.searchEmail],
      searchEmail: ''
    });
  };

  const removeManualEmail = (email) => {
    setFormData({
      ...formData,
      manualEmails: formData.manualEmails.filter(e => e !== email)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        name: formData.nombreMateria,
        diploma: formData.diplomaturaId,
        teachers: formData.teachers,
        teachers_emails: formData.manualEmails
      };

      if (isEditing && selectedSubjectId) {
        await api.patch(`subjects/${selectedSubjectId}/`, payload);
        alert(`Materia "${formData.nombreMateria}" actualizada exitosamente`);
      } else {
        await api.post('subjects/', payload);
        alert(`Materia "${formData.nombreMateria}" creada exitosamente`);
      }
      onClose();
    } catch (err) {
      console.error('Error creating subject:', err);
      alert('Error al crear la materia: ' + (JSON.stringify(err.response && err.response.data) || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
      <div className={`modal-dialog modal-dialog-centered ${isFullscreen ? 'modal-fullscreen' : 'modal-lg'}`}>
        <div className="modal-content glassmorphism shadow-lg border-0">
          <div className="modal-header border-bottom border-secondary border-opacity-10 pb-3 d-flex justify-content-between align-items-center">
            <h5 className="modal-title section-title fs-4 mb-0 d-flex align-items-center">
              <BookOpen size={24} className={`me-2 ${isEditing ? 'text-primary' : 'text-warning'}`} /> {isEditing ? 'Editar Materia' : 'Materias'}
            </h5>
            <div className="d-flex align-items-center gap-2 ms-auto">
              <button
                type="button"
                className="btn-glass p-1 d-flex align-items-center justify-content-center"
                onClick={() => setIsFullscreen(f => !f)}
                title={isFullscreen ? 'Contraer' : 'Ampliar a pantalla completa'}
                style={{ width: '32px', height: '32px', borderRadius: '8px' }}
              >
                {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </button>
              <button
                type="button"
                className="btn-close-premium"
                onClick={onClose}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
          </div>
          <div className="modal-body p-4">
            {activeTab === 'tabs' ? (
              <div className="row g-4 mb-2">
                <div className="col-md-6">
                  <button 
                    onClick={() => {
                      setIsEditing(false);
                      setSelectedSubjectId(null);
                      setFormData({
                        nombreMateria: '',
                        diplomaturaId: '',
                        teachers: [],
                        manualEmails: [],
                        searchEmail: ''
                      });
                      setActiveTab('form');
                    }}
                    className="btn btn-outline-primary w-100 py-5 d-flex flex-column align-items-center gap-3 border-2 shadow-sm hover-lift"
                  >
                    <BookOpen size={48} className="text-warning" />
                    <div className="text-center">
                      <div className="fw-bold fs-5">Nueva Materia</div>
                      <div className="small text-muted">Crear una materia desde cero</div>
                    </div>
                  </button>
                </div>
                <div className="col-md-6">
                  <button 
                    onClick={() => setActiveTab('list')}
                    className="btn btn-outline-primary w-100 py-5 d-flex flex-column align-items-center gap-3 border-2 shadow-sm hover-lift"
                  >
                    <Pencil size={48} className="text-primary" />
                    <div className="text-center">
                      <div className="fw-bold fs-5">Editar Materia</div>
                      <div className="small text-muted">Modificar una materia existente</div>
                    </div>
                  </button>
                </div>
              </div>
            ) : activeTab === 'list' ? (
              <div className="animate-fade-in">
                <div className="mb-4">
                  <div className="input-group">
                    <span className="input-group-text"><BookOpen size={18}/></span>
                    <input 
                      type="text" 
                      className="form-control" 
                      placeholder="Buscar materia por nombre o diplomatura..." 
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="list-group list-group-flush border rounded shadow-sm overflow-auto" style={{ maxHeight: '400px' }}>
                  {subjects.filter(s => 
                    s.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                    s.diploma_name.toLowerCase().includes(searchTerm.toLowerCase())
                  ).length > 0 ? subjects.filter(s => 
                    s.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                    s.diploma_name.toLowerCase().includes(searchTerm.toLowerCase())
                  ).map(s => (
                    <button 
                      key={s.id}
                      onClick={() => {
                        setIsEditing(true);
                        setSelectedSubjectId(s.id);
                        setFormData({
                          nombreMateria: s.name,
                          diplomaturaId: s.diploma,
                          teachers: s.teachers || [],
                          manualEmails: [],
                          searchEmail: ''
                        });
                        setActiveTab('form');
                      }}
                      className="list-group-item list-group-item-action d-flex justify-content-between align-items-center py-3"
                    >
                      <div>
                        <div className="fw-bold">{s.name}</div>
                        <div className="small text-muted">{s.diploma_name} ({s.diploma_municipio})</div>
                      </div>
                      <Pencil size={16} className="text-primary" />
                    </button>
                  )) : (
                    <div className="p-4 text-center text-muted italic">No se encontraron materias.</div>
                  )}
                </div>
                <button 
                  className="btn btn-link mt-3 text-decoration-none d-flex align-items-center gap-2" 
                  onClick={() => setActiveTab('tabs')}
                >
                  <ArrowLeft size={16} /> Volver al inicio
                </button>
              </div>
            ) : (
              <div className="animate-fade-in">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <p className="text-muted mb-0">
                    {isEditing ? 'Modifique los datos de la materia y asigne los docentes correspondientes.' : 'Especifique el nombre, la diplomatura y los docentes que estarán a cargo de la materia.'}
                  </p>
                  <button 
                    className="btn btn-sm btn-outline-secondary" 
                    onClick={() => setActiveTab('tabs')}
                  >
                    Cambiar Modo
                  </button>
                </div>
                
                <form onSubmit={handleSubmit}>
                  <div className="row g-4">
                    <div className="col-md-6">
                      <div className="mb-4 text-start">
                          <label className="form-label fw-bold text-dark small text-uppercase tracking-wider">Diplomatura</label>
                          <select 
                            className="form-select form-select-lg shadow-sm border-2" 
                            value={formData.diplomaturaId} 
                            onChange={e => setFormData({...formData, diplomaturaId: e.target.value})} 
                            required
                          >
                            <option value="">Seleccione una diplomatura...</option>
                            {diplomaturas.map(d => (
                              <option key={d.id} value={d.id}>
                                {d.name} ({d.municipio})
                              </option>
                            ))}
                          </select>
                      </div>

                      <div className="mb-4 text-start">
                          <label className="form-label fw-bold text-dark small text-uppercase tracking-wider">Nombre de la Materia</label>
                          <input 
                            type="text" 
                            className="form-control form-control-lg shadow-sm border-2" 
                            placeholder="Ej: Programación Avanzada" 
                            value={formData.nombreMateria} 
                            onChange={e => setFormData({...formData, nombreMateria: e.target.value})} 
                            required 
                          />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-2 text-start d-flex justify-content-between align-items-center">
                          <label className="form-label fw-bold text-dark small text-uppercase tracking-wider mb-0">Profesores a Cargo</label>
                          <button 
                            type="button" 
                            className="btn btn-sm btn-outline-primary border-0 fw-bold"
                            onClick={() => setShowManualInput(!showManualInput)}
                          >
                            {showManualInput ? '- Cancelar' : '+ Invitar por Email'}
                          </button>
                      </div>

                      {showManualInput && (
                        <div className="input-group mb-3 animate-fade-in">
                          <input 
                            type="email" 
                            className="form-control" 
                            placeholder="correo@ejemplo.com"
                            value={formData.searchEmail}
                            onChange={e => setFormData({...formData, searchEmail: e.target.value})}
                          />
                          <button className="btn btn-primary" type="button" onClick={addManualEmail}>
                            Agregar
                          </button>
                        </div>
                      )}

                      <div className="border-2 border rounded-3 bg-light p-3 shadow-inner" style={{ minHeight: '180px', maxHeight: '300px', overflowY: 'auto' }}>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          {formData.manualEmails.map(email => (
                            <span key={email} className="badge bg-primary d-flex align-items-center gap-2 py-2 px-3 rounded-pill">
                              {email}
                              <X size={14} className="cursor-pointer" onClick={() => removeManualEmail(email)} />
                            </span>
                          ))}
                        </div>

                        <div className="list-group list-group-flush">
                          {profesores.length > 0 ? profesores.map(p => (
                            <label key={p.id} className="list-group-item list-group-item-action d-flex align-items-center gap-3 border-0 rounded-2 mb-1 px-2 py-2 cursor-pointer">
                              <input 
                                className="form-check-input flex-shrink-0" 
                                type="checkbox" 
                                checked={formData.teachers.includes(p.id)}
                                onChange={() => toggleTeacher(p.id)}
                              />
                              <div className="d-flex flex-column overflow-hidden">
                                <span className="fw-bold text-dark text-truncate" style={{ fontSize: '0.9rem' }}>
                                  {p.first_name || p.last_name ? `${p.first_name} ${p.last_name}` : 'Sin Nombre'}
                                </span>
                                <small className="text-muted text-truncate" style={{ fontSize: '0.75rem' }}>{p.email}</small>
                              </div>
                            </label>
                          )) : (
                            <div className="text-center py-4 text-muted small fst-italic">
                              No se encontraron docentes disponibles.
                            </div>
                          )}
                        </div>
                      </div>
                      <small className="text-muted d-block mt-2">Seleccione uno o más docentes de la lista o invite por correo.</small>
                    </div>
                  </div>

                  <div className="d-flex justify-content-end gap-3 mt-5 pt-3 border-top border-secondary border-opacity-10">
                      <button type="button" className="btn btn-glass px-4 py-2 fw-bold text-dark d-flex align-items-center gap-2" onClick={onClose} disabled={loading}>
                          <ArrowLeft size={18}/> Cancelar
                      </button>
                      <button type="submit" className="btn btn-warning px-5 py-2 text-dark fw-bold d-flex align-items-center gap-2 shadow" disabled={loading}>
                          {loading ? (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          ) : (
                            <Save size={18}/>
                          )}
                          {loading ? (isEditing ? 'Actualizando...' : 'Creando...') : (isEditing ? 'Guardar Cambios' : 'Confirmar Materia')}
                      </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateMateriaMenu;
